import { DocumentPlusIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { FunctionComponent, ReactNode, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useController } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ErrorMessage } from 'softcat:components/error-message.tsx';
import { Form } from 'softcat:components/form.tsx';

type FormFileUploadProperties = {
    accept: string;
    label: ReactNode;
    name: string;
};

export const FormFileUpload: FunctionComponent<FormFileUploadProperties> = ({ accept, label, name }) => {
    const { field, fieldState } = useController({ name });
    const [isDragActive, setIsDragActive] = useState(false);
    const [details, setDetails] = useState<{ name: string; size: number }>();

    const { getRootProps, getInputProps } = useDropzone({
        /* v8 ignore start */
        onDragEnter: () => {
            return setIsDragActive(true);
        },
        onDragLeave: () => {
            return setIsDragActive(false);
        },
        /* v8 ignore stop */
        onDrop: (files) => {
            setIsDragActive(false);

            if (files.length > 0) {
                const file = files[0];

                field.onChange(file);
                field.onBlur();

                setDetails({ name: file.name, size: file.size });
            }
        },
    });

    return (
        <div className="sm:grid sm:grid-cols-4 sm:items-start sm:gap-4">
            <Form.Label htmlFor={name}>{label}</Form.Label>

            <div
                className={clsx('mt-2 sm:col-span-3 sm:mt-0', {
                    'text-gray-400': !fieldState.error,
                    'text-red-600': fieldState.error,
                })}
            >
                <div
                    {...getRootProps()}
                    className={clsx('relative flex cursor-pointer flex-col space-y-3 rounded-lg border px-6 py-10 text-center font-semibold shadow-sm', {
                        'bg-gray-100': isDragActive,
                        'border-gray-200 text-gray-300 hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-500': !fieldState.error,
                        'border-red-600 text-red-600 ring-red-600 hover:border-red-400 hover:bg-red-50 hover:text-red-500 focus:ring-red-600': fieldState.error,
                    })}
                >
                    {details ? (
                        <p className={'text-center'}>
                            <DocumentPlusIcon aria-hidden="true" className="mx-auto mb-1 block size-6" /> {details.name} (
                            {details.size / 1024 < 1024 ? `${(details.size / 1024).toFixed(2)}KB` : `${(details.size / (1024 * 1024)).toFixed(2)}MB`})
                        </p>
                    ) : (
                        <>
                            <p>
                                <FormattedMessage id={'FORM_FILE_UPLOAD_1'} />
                            </p>
                            <p className="text-xs">
                                <FormattedMessage id={'FORM_FILE_UPLOAD_2'} values={{ extensions: accept }} />
                            </p>
                        </>
                    )}

                    <input id={name} {...getInputProps()} />
                </div>

                {fieldState.error && (
                    <div className="mt-3">
                        <ErrorMessage>{fieldState.error.message}</ErrorMessage>
                    </div>
                )}
            </div>
        </div>
    );
};
